<script setup lang="ts">
import { VueDraggableNext as draggable } from 'vue-draggable-next'
import { AlignHorizontalCenters, AlignLeftBox, AlignRightBox } from '@iconoir/vue'
import { GripVertical, Trash2, Plus } from 'lucide-vue-next'
import type { Component } from 'vue'

import EmailPanelSection from '../EmailPanelSection.vue'
import ColorPickerWithPresets from '../ColorPickerWithPresets.vue'
import SocialNetworkIcon from './SocialNetworkIcon.vue'
import { SOCIAL_URL_CONFIGS, buildSocialUrl, extractSocialHandle, matchesSocialDomain } from '../../utils/socialUrls'

import { buildLinkValue, detectLinkVariable, linkVariables } from '../../constants/linkVariables'
import type { Alignment, IconSet, IconStyle, SocialBlock, SocialLink, SocialNetwork } from '../../types'

const { t } = useI18n()

const props = defineProps<{
  block: SocialBlock
  colorPresets?: { color: string; label: string }[]
}>()

const emit = defineEmits<{
  (e: 'update:block', block: SocialBlock): void
}>()

const updateBlock = (patch: Partial<SocialBlock>) => {
  emit('update:block', { ...props.block, ...patch })
}

// Local mutable copy for drag reorder — syncs on any change (add/remove/network switch)
const localLinks = ref<SocialLink[]>(props.block.links.map((l) => ({ ...l })))

watch(
  () => props.block.links,
  (newLinks) => {
    localLinks.value = newLinks.map((l) => ({ ...l }))
  },
  { deep: true }
)

const onDragEnd = () => {
  updateBlock({ links: [...localLinks.value] })
}

const updateLink = (id: string, patch: Partial<SocialLink>) => {
  updateBlock({
    links: props.block.links.map((l) => (l.id === id ? { ...l, ...patch } : l))
  })
}

const removeLink = (id: string) => {
  updateBlock({ links: props.block.links.filter((l) => l.id !== id) })
}

// Liens sociaux trackés : on stocke {{customLink(url)}} comme le bouton, le back
// génère le short link à l'envoi. L'input affiche l'URL nue (cf. EmailLinkInput).
const customLinkVariable = linkVariables.find((v) => v.key === 'customLink') ?? linkVariables[0]

const displaySocialUrl = (url: string) => detectLinkVariable(url)?.url ?? url

const wrapSocialUrl = (raw: string) => {
  const trimmed = raw.trim()
  if (!trimmed) return ''
  // Déjà wrappé (template chargé / re-saisie) → on n'empile pas un second wrapper.
  if (detectLinkVariable(trimmed)) return trimmed
  return buildLinkValue(customLinkVariable, trimmed)
}

const addLink = (network: SocialNetwork) => {
  const newLink: SocialLink = {
    id: `social-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    network,
    url: ''
  }
  updateBlock({ links: [...props.block.links, newLink] })
}

const SOCIAL_ICON_SIZES: { value: number; labelKey: string }[] = [
  { value: 24, labelKey: 'socialSizeSm' },
  { value: 40, labelKey: 'socialSizeMd' },
  { value: 56, labelKey: 'socialSizeLg' }
]

const changeNetwork = (link: SocialLink, network: SocialNetwork) => {
  if (network === 'website') {
    updateLink(link.id, { network })
    return
  }
  const bare = displaySocialUrl(link.url)
  // Depuis Website on ne transfère que si l'URL est bien celle du réseau cible :
  // un site (monresto.fr) ne doit pas devenir un faux handle Instagram.
  const handle =
    link.network === 'website'
      ? matchesSocialDomain(network, bare)
        ? extractSocialHandle(network, bare)
        : ''
      : extractSocialHandle(link.network, bare)
  updateLink(link.id, { network, url: wrapSocialUrl(buildSocialUrl(network, handle)) })
}

const handlePlaceholder = (network: SocialNetwork) =>
  network === 'whatsapp' ? '33612345678' : t('pages.email.emailTemplateCreatePage.panel.socialHandlePlaceholder')

const ICON_STYLES: { value: IconStyle; labelKey: string }[] = [
  { value: 'filled', labelKey: 'socialStyleFilled' },
  { value: 'outline', labelKey: 'socialStyleOutline' },
  { value: 'plain', labelKey: 'socialStylePlain' }
]

const ICON_SET_OPTIONS: { value: IconSet; labelKey: string }[] = [
  { value: 'brand', labelKey: 'socialSetBrand' },
  { value: 'dark', labelKey: 'socialSetDark' },
  { value: 'light', labelKey: 'socialSetLight' }
]

const NETWORK_OPTIONS: { value: SocialNetwork; label: string }[] = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'twitter', label: 'X (Twitter)' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'pinterest', label: 'Pinterest' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'website', label: t('pages.email.emailTemplateCreatePage.panel.socialNetworkWebsite') }
]

// Networks not yet used — offered in the "add link" combobox (one link per network).
const availableNetworks = computed(() => {
  const used = new Set(props.block.links.map((l) => l.network))
  return NETWORK_OPTIONS.filter((o) => !used.has(o.value))
})

const optionsForLink = (current: SocialNetwork) => {
  const usedByOthers = new Set(props.block.links.filter((l) => l.network !== current).map((l) => l.network))
  return NETWORK_OPTIONS.filter((o) => o.value === current || !usedByOthers.has(o.value))
}

const onPickNetwork = (network: SocialNetwork | null) => {
  if (network) addLink(network)
}

const alignments: { value: Alignment; icon: Component }[] = [
  { value: 'left', icon: AlignLeftBox },
  { value: 'center', icon: AlignHorizontalCenters },
  { value: 'right', icon: AlignRightBox }
]
</script>

<template>
  <Teleport to="#email-settings-panel" defer>
    <div class="space-y-8 px-4 pb-6 pt-4">
      <!-- Fond de bloc -->
      <EmailPanelSection :title="t('pages.email.emailTemplateCreatePage.panel.blockBackground')">
        <ColorPickerWithPresets
          :model-value="block.blockBackgroundColor ?? 'ffffff'"
          :presets="colorPresets ?? []"
          @update:model-value="(v) => updateBlock({ blockBackgroundColor: v })"
        />
      </EmailPanelSection>

      <!-- Set de couleur -->
      <EmailPanelSection :title="t('pages.email.emailTemplateCreatePage.panel.color')">
        <Tabs :model-value="block.iconSet" @update:model-value="(v) => updateBlock({ iconSet: v as IconSet })">
          <TabsList class="w-full">
            <TabsTrigger v-for="s in ICON_SET_OPTIONS" :key="s.value" :value="s.value" class="flex-1">
              {{ t(`pages.email.emailTemplateCreatePage.panel.${s.labelKey}`) }}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </EmailPanelSection>

      <!-- Style -->
      <EmailPanelSection :title="t('pages.email.emailTemplateCreatePage.panel.socialIconStyle')">
        <Tabs :model-value="block.iconStyle" @update:model-value="(v) => updateBlock({ iconStyle: v as IconStyle })">
          <TabsList class="w-full">
            <TabsTrigger v-for="s in ICON_STYLES" :key="s.value" :value="s.value" class="flex-1">
              {{ t(`pages.email.emailTemplateCreatePage.panel.${s.labelKey}`) }}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </EmailPanelSection>

      <!-- Taille -->
      <EmailPanelSection :title="t('pages.email.emailTemplateCreatePage.panel.socialIconSize')">
        <Tabs :model-value="String(block.iconSize)" @update:model-value="(v) => updateBlock({ iconSize: Number(v) })">
          <TabsList class="w-full">
            <TabsTrigger v-for="s in SOCIAL_ICON_SIZES" :key="s.value" :value="String(s.value)" class="flex-1">
              {{ t(`pages.email.emailTemplateCreatePage.panel.${s.labelKey}`) }}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </EmailPanelSection>

      <!-- Alignement -->
      <EmailPanelSection :title="t('pages.email.emailTemplateCreatePage.panel.globalAlignment')">
        <Tabs :model-value="block.align" @update:model-value="(v) => updateBlock({ align: v as Alignment })">
          <TabsList class="w-full">
            <TabsTrigger v-for="opt in alignments" :key="opt.value" :value="opt.value" class="flex-1">
              <component :is="opt.icon" />
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </EmailPanelSection>

      <!-- Liens -->
      <EmailPanelSection :title="t('pages.email.emailTemplateCreatePage.panel.socialLinks')">
        <draggable :list="localLinks" handle=".drag-handle" :animation="150" class="space-y-2" @end="onDragEnd">
          <div v-for="link in localLinks" :key="link.id" class="rounded-lg border border-border bg-white shadow-sm">
            <!-- Header card : icône + select réseau + poubelle + drag handle -->
            <div class="flex items-center gap-2 px-3 py-2">
              <SocialNetworkIcon
                :network="link.network"
                :set="block.iconSet"
                :size="22"
                :icon-style="block.iconStyle"
                class="shrink-0"
              />
              <Select :model-value="link.network" @update:model-value="(v) => changeNetwork(link, v as SocialNetwork)">
                <SelectTrigger
                  class="h-7 flex-1 border-0 bg-transparent p-0 text-sm font-medium text-foreground shadow-none focus:ring-0"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="opt in optionsForLink(link.network)" :key="opt.value" :value="opt.value">
                    {{ opt.label }}
                  </SelectItem>
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                class="shrink-0 text-muted-foreground hover:text-red-500"
                :disabled="block.links.length <= 1"
                @click="removeLink(link.id)"
              >
                <Trash2 class="size-4" />
              </Button>
              <GripVertical
                class="drag-handle size-4 shrink-0 cursor-grab text-muted-foreground active:cursor-grabbing"
              />
            </div>

            <!-- Handle input avec préfixe d'URL fixe (URL libre pour website) -->
            <div class="border-t border-border px-3 pb-3 pt-2">
              <Input
                v-if="link.network === 'website'"
                :model-value="displaySocialUrl(link.url)"
                :placeholder="t('pages.email.emailTemplateCreatePage.panel.socialLinkPlaceholder')"
                class="h-8 text-sm"
                @update:model-value="(v) => updateLink(link.id, { url: wrapSocialUrl(String(v)) })"
              />
              <div
                v-else
                class="border-input focus-within:border-ring focus-within:ring-ring/50 flex h-8 w-full items-center rounded-md border bg-background shadow-xs transition-[color,box-shadow] focus-within:ring-[3px]"
              >
                <span class="text-muted-foreground shrink-0 select-none pl-3 text-sm">
                  {{ SOCIAL_URL_CONFIGS[link.network]?.prefix }}
                </span>
                <Input
                  :model-value="extractSocialHandle(link.network, displaySocialUrl(link.url))"
                  :placeholder="handlePlaceholder(link.network)"
                  class="h-full border-0 bg-transparent px-1 text-sm shadow-none focus-visible:border-0 focus-visible:ring-0"
                  @update:model-value="
                    (v) =>
                      updateLink(link.id, {
                        url: wrapSocialUrl(buildSocialUrl(link.network, displaySocialUrl(String(v))))
                      })
                  "
                />
              </div>
            </div>
          </div>
        </draggable>

        <!-- Bouton ajouter : ComboboxSelect (recherche intégrée) de choix du réseau -->
        <ComboboxSelect
          :model-value="null"
          :options="availableNetworks"
          :disabled="availableNetworks.length === 0"
          trigger-class="mt-3"
          @update:model-value="(v) => onPickNetwork(v as SocialNetwork | null)"
        >
          <template #trigger>
            <span class="flex items-center gap-2 text-foreground-secondary">
              <Plus class="size-4" />
              {{ t('pages.email.emailTemplateCreatePage.panel.socialAddLink') }}
            </span>
          </template>
          <template #option="{ option }">
            <span class="flex items-center gap-2">
              <SocialNetworkIcon
                :network="option.value as SocialNetwork"
                set="dark"
                :size="20"
                icon-style="plain"
                class="shrink-0"
              />
              {{ option.label }}
            </span>
          </template>
        </ComboboxSelect>
      </EmailPanelSection>
    </div>
  </Teleport>
</template>
